/**
 * Column definitions for the Contacts (Marketing/Leads) list tables.
 * All field names verified live against GET /contacts (api-labs-datacentral).
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import type { ContactRow } from '@/api/contacts';

/* ── Status mapping ─────────────────────────────────────────────────────── */

function statusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'warm-lead': return 'warning';
    case 'sale-qualified-lead': return 'success';
    case 'cool-lead': return 'info';
    case 'un-qualified-lead': return 'danger';
    case 'UNCLASSIFIED': return 'muted';
    default: return 'neutral';
  }
}

function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'warm-lead': return 'Warm Lead';
    case 'sale-qualified-lead': return 'SQL';
    case 'cool-lead': return 'Cool Lead';
    case 'un-qualified-lead': return 'Unqualified';
    case 'UNCLASSIFIED': return 'Unclassified';
    default: return status ?? '—';
  }
}

function channelLabel(channel: string | null | undefined): string {
  switch (channel?.toLowerCase()) {
    case 'livechat': return 'Live Chat';
    case 'fb_messenger': return 'FB Messenger';
    case 'fb_ads': return 'FB Ads';
    case 'gg_ads': return 'Google Ads';
    case 'form': return 'Form';
    case 'hotline': return 'Hotline';
    case 'event': return 'Event';
    case 'email': return 'Email';
    case 'forms': return 'Form';
    default: return channel ?? '—';
  }
}

/* ── Column defs ─────────────────────────────────────────────────────────── */

export type ContactCol = ColumnDef<ContactRow, unknown>;

export const contactColumns: ContactCol[] = [
  {
    id: 'contact_source_name',
    header: 'Name',
    cell: ({ row }) => (
      <span
        style={{
          fontWeight: 'var(--vn-font-weight-medium)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          maxWidth: 220,
        }}
      >
        {row.original.contact_source_name || '—'}
      </span>
    ),
  },
  {
    id: 'contact_source_email',
    header: 'Email',
    cell: ({ row }) => (
      <span style={{ whiteSpace: 'nowrap' }}>{row.original.contact_source_email || '—'}</span>
    ),
  },
  {
    id: 'contact_source_phone',
    header: 'Phone',
    cell: ({ row }) => row.original.contact_source_phone || '—',
  },
  {
    id: 'contact_source_company',
    header: 'Company',
    cell: ({ row }) => (
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          maxWidth: 180,
        }}
      >
        {row.original.contact_source_company || '—'}
      </span>
    ),
  },
  {
    id: 'contact_source_channel',
    header: 'Channel',
    cell: ({ row }) => channelLabel(row.original.contact_source_channel),
  },
  {
    id: 'contact_source_status',
    header: 'Status',
    cell: ({ row }) => {
      const s = row.original.contact_source_status;
      return <StatusPill tone={statusTone(s)}>{statusLabel(s)}</StatusPill>;
    },
  },
  {
    id: 'assignee',
    header: 'Assignee',
    cell: ({ row }) => {
      const names = row.original.contact_source_assignee_name;
      if (!names?.length) return <span style={{ color: 'var(--vn-fg-subtle)' }}>Unassigned</span>;
      return names.map((a) => a.employee_name).join(', ');
    },
  },
  {
    id: 'contact_source_interested_services',
    header: 'Interested Services',
    cell: ({ row }) => {
      const services = row.original.contact_source_interested_services;
      if (!services?.length) return '—';
      return (
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: 200,
          }}
          title={services.join(', ')}
        >
          {services.join(', ')}
        </span>
      );
    },
  },
  {
    id: 'converted',
    header: 'Converted',
    cell: ({ row }) =>
      row.original.converted ? (
        <StatusPill tone="success">Yes</StatusPill>
      ) : (
        <StatusPill tone="muted">No</StatusPill>
      ),
  },
  {
    id: 'contact_source_created_at',
    header: 'Created',
    cell: ({ row }) => {
      const raw = row.original.contact_source_created_at;
      if (!raw) return '—';
      const d = new Date(raw);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
];
